// In-dashboard Playground — boots the same @cortex/widget customers will
// embed on their site, but pointed at the operator's own agent. The "wow
// moment" of the tour: a non-tech user clicks Try a sample → lands here →
// asks a question → sees the answer in the same chat UI their visitors
// will use. Without this, they'd have to leave the dashboard, build a
// static HTML page, and run a local server to verify anything.
//
// Mechanics:
//
//   1. On mount, mint a session via POST /v1/agents/:id/playground-session.
//      That endpoint bypasses the public Origin allowlist (the dashboard
//      origin is intentionally NOT in the agent's allowed_origins — the
//      list is for public embedding domains only).
//   2. Hand the session token + the dashboard's API base + the agent id
//      to initCortexWidget(), telling it to mount inside our container
//      div instead of attaching to body.
//   3. On unmount (or agent change), call handle.destroy() so the widget
//      cleans up its Preact tree + event listeners.

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { RotateCcw, ShieldAlert } from 'lucide-react';
import { initCortexWidget, type MountHandle } from '@cortex/widget';
import type { Agent, MintPlaygroundSessionResult } from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import { ApiError } from '../../lib/api-client.js';
import { agentsApi } from '../../lib/api/agents.js';
import { useResumeSubscription } from '../../lib/api/billing.js';
import { usageApi, USAGE_QUERY_KEY } from '../../lib/api/usage.js';
import { deriveSubscriptionState } from '../../lib/subscription-state.js';
import { env } from '../../env.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import { Button } from '../../components/ui/button.js';

interface PlaygroundProps {
  agent: Agent;
}

export function PlaygroundCard({ agent }: PlaygroundProps): React.JSX.Element {
  const api = useApiClient();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<MountHandle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<MintPlaygroundSessionResult | null>(null);

  // Pre-check the subscription state. The mint endpoint isn't on the chat-
  // limits plugin (operator self-testing shouldn't burn plan quota even on
  // a canceled sub), so mint would succeed and the user would only see the
  // 402 when they typed a message. Catching it here lets us replace the
  // widget mount with a clear retention message instead of letting the
  // widget render a generic error inside the chat surface.
  const usage = useQuery({
    queryKey: USAGE_QUERY_KEY,
    queryFn: () => usageApi.summary(api),
  });
  const blockedState: 'canceled' | 'past-due' | null = (() => {
    if (!usage.data) return null;
    const s = deriveSubscriptionState(usage.data.plan);
    return s === 'canceled' || s === 'past-due' ? s : null;
  })();

  const mint = useMutation({
    mutationFn: () => agentsApi.mintPlaygroundSession(api, agent.id),
    onSuccess: (data) => {
      setError(null);
      setSession(data);
    },
    onError: (err) => {
      const status = err instanceof ApiError ? err.status : undefined;
      const message = err instanceof Error ? err.message : 'Could not start playground.';
      setError(status !== undefined ? `${String(status)}: ${message}` : message);
    },
  });

  // Mint on first mount. Re-mint when the agent id changes (operator
  // navigates between agents while the page is open). Reading `mutate`
  // directly (it's stable across mutation re-renders per react-query)
  // keeps the deps array honest without needing an effect re-run on
  // every mutation state change. Skip when blocked — minting on a
  // canceled sub would still succeed but the widget would 402 on first
  // message; we don't want to even bother.
  const mintMutate = mint.mutate;
  useEffect(() => {
    if (blockedState !== null) return;
    mintMutate();
  }, [agent.id, mintMutate, blockedState]);

  // Mount the widget once we have a session. Tear it down on unmount or
  // re-mount so we never have stale Preact trees attached.
  useEffect(() => {
    if (!session || !containerRef.current) return;
    // Defensive: destroy any prior handle (e.g. if session changed mid-life).
    handleRef.current?.destroy();
    try {
      handleRef.current = initCortexWidget({
        apiBase: env.apiBaseUrl,
        agentId: agent.id,
        sessionToken: session.sessionToken,
        // Mount inline (no shadow DOM) so the playground card visually
        // contains the widget. The shadow-DOM path is what customers get
        // on their own site; here we want predictable layout inside our
        // own page.
        container: containerRef.current,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not initialise widget.';
      setError(message);
    }
    return () => {
      handleRef.current?.destroy();
      handleRef.current = null;
    };
  }, [session, agent.id]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Playground</CardTitle>
        <p className="text-sm text-muted-foreground">
          This is exactly what your visitors will see when you embed Cortex on your site.
          Ask the assistant a question to test it.
        </p>
      </CardHeader>
      <CardContent>
        {blockedState !== null ? (
          <BlockedRetentionPanel state={blockedState} />
        ) : (
          <>
            {mint.isPending && !session && (
              <div className="text-sm text-muted-foreground">Starting playground…</div>
            )}
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            {/* Container: minimum height so the empty state of the widget
                doesn't collapse the card to nothing. The widget itself
                fills the container.
                `isolate` (= `isolation: isolate`) creates a fresh stacking
                context that bounds the widget's max-int z-index (2147483000,
                intentional so the widget floats above any host page in
                production). Without isolation it escapes to the document
                root and renders above Clerk's dropdown portal; with it,
                the widget is contained and Clerk's portal at body level
                paints on top as expected. */}
            <div
              ref={containerRef}
              className="relative isolate min-h-[480px] rounded-md border border-muted-foreground/15"
              aria-label="Cortex assistant playground"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Replaces the widget mount when the tenant's subscription is in a state
// that the chat gate would reject. Keeps the playground card frame so the
// page geometry doesn't jump, but swaps the chat surface for a clear
// "service paused" message + Resume CTA. Without this, the operator would
// see "Starting playground…" then a generic 402 error inside the widget
// and have to navigate to /billing to figure out what happened.
function BlockedRetentionPanel({ state }: { state: 'canceled' | 'past-due' }) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resume = useResumeSubscription();

  const message =
    state === 'canceled'
      ? 'Service paused. Reactivate your subscription to continue testing this agent — your data is preserved.'
      : "Payment failed. Update your card in the Stripe Customer Portal to keep service running.";

  const onResume = () => {
    setError(null);
    resume.mutate(undefined, {
      onSuccess: () => { setConfirming(false); },
      onError: (err) => {
        if (err instanceof ApiError) {
          if (err.status === 404) {
            setError('Stripe billing not configured.');
            return;
          }
          if (err.status === 402 || err.status === 409) {
            setError('Subscription state changed. Refresh and try again.');
            return;
          }
        }
        setError(err instanceof Error ? err.message : 'Could not resume.');
      },
    });
  };

  return (
    <div
      role="alert"
      className="flex min-h-[480px] flex-col items-center justify-center gap-4 rounded-md border border-destructive/30 bg-destructive/5 p-8 text-center"
    >
      <ShieldAlert className="h-10 w-10 text-destructive" aria-hidden />
      <p className="max-w-md text-sm text-muted-foreground">{message}</p>
      {state === 'canceled' ? (
        confirming ? (
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button onClick={onResume} disabled={resume.isPending} variant="default">
              {resume.isPending ? 'Resuming…' : 'Confirm reactivate'}
            </Button>
            <Button
              onClick={() => { setConfirming(false); }}
              disabled={resume.isPending}
              variant="ghost"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button onClick={() => { setConfirming(true); }} variant="default">
              <RotateCcw aria-hidden />
              Reactivate subscription
            </Button>
            <Button asChild variant="ghost">
              <Link to="/billing">Manage billing</Link>
            </Button>
          </div>
        )
      ) : (
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button asChild variant="default">
            <Link to="/billing">Update payment method</Link>
          </Button>
        </div>
      )}
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
